import React, { useState, useEffect } from 'react';
import { IGetModifierGroupsTemplateInfo, IGetModifierGroupsShortResponse } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierGroupsShortByCustomerId } from 'backend/modifierGroups';
import { MultipleSelect } from './MultipleSelect';
import { DnDList } from './DnDList';
import style from './ModifierGroupsMultipleSelect.module.scss';

interface IProps {
	setModifierGroups: (value: string[]) => void;
	setModifierGroupsWithAdditionalData: (value: IGetModifierGroupsTemplateInfo['templates']) => void;
	modifierGroups: string[];
	modifierGroupsWithAdditionalData: IGetModifierGroupsTemplateInfo['templates'];
	customerID?: string;
}

export const ModifierGroupsMultipleSelect: React.FC<IProps> = React.memo(
	({
		setModifierGroups,
		setModifierGroupsWithAdditionalData,
		modifierGroups,
		modifierGroupsWithAdditionalData,
		customerID,
	}) => {
		const [isFirstRender, setIsFirstRender] = useState(true);
		const { response, loading, refetch, responseError } = useFetchData<IGetModifierGroupsShortResponse, string>(
			customerID ? getModifierGroupsShortByCustomerId : null,
			customerID || '',
		);

		useEffect(() => {
			if (!isFirstRender) {
				refetch();
			} else {
				setIsFirstRender(false);
			}
		}, [customerID]);

		const handleSetModifierGroups = (value: string[]) => {
			const updatedModifierGroupsWithAdditionalData = value.map((modifierGroupId) => {
				const groupFromPreviousState = modifierGroupsWithAdditionalData.find(
					(exist) => exist.id === modifierGroupId,
				);

				const newGroup = {
					id: modifierGroupId,
					name:
						response?.data.modifierGroups.find((modifierGroup) => modifierGroup.id === modifierGroupId)
							?.name || '-',
					downLimit: null,
					upLimit: null,
					isLimitsProtected: false,
					isAdditional: false,
				};
				return groupFromPreviousState ? groupFromPreviousState : newGroup;
			});

			setModifierGroups(value);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		const changeDownLimit = (id: string, value: Nullable<number>) => {
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.map((el) =>
				el.id === id ? { ...el, downLimit: value } : el,
			);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		const changeUpLimit = (id: string, value: Nullable<number>) => {
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.map((el) =>
				el.id === id ? { ...el, upLimit: value } : el,
			);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		const toggleIsLimitsProtected = (id: string, value: boolean) => {
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.map((el) =>
				el.id === id ? { ...el, isLimitsProtected: value } : el,
			);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		const toggleIsAdditional = (id: string, value: boolean) => {
			const updatedModifierGroupsWithAdditionalData = modifierGroupsWithAdditionalData.map((el) =>
				el.id === id ? { ...el, isAdditional: value } : el,
			);
			setModifierGroupsWithAdditionalData(updatedModifierGroupsWithAdditionalData);
		};

		return (
			<div className={style.container} data-test="ModifierGroupsMultipleSelect">
				<MultipleSelect
					data={response?.data.modifierGroups || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					setModifierGroups={handleSetModifierGroups}
					modifierGroups={modifierGroups}
				/>
				<DnDList
					setModifierGroups={setModifierGroups}
					setModifierGroupsWithAdditionalData={setModifierGroupsWithAdditionalData}
					changeDownLimit={changeDownLimit}
					changeUpLimit={changeUpLimit}
					toggleIsLimitsProtected={toggleIsLimitsProtected}
					toggleIsAdditional={toggleIsAdditional}
					modifierGroups={modifierGroups}
					modifierGroupsWithAdditionalData={modifierGroupsWithAdditionalData}
				/>
			</div>
		);
	},
);
